"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { Smartphone, ShieldCheck, Headset, RefreshCcw, ArrowRight, MessageCircle } from "lucide-react";

/**
 * Re-digital — combined home tail sections: compatibility preview, support
 * reassurance, and the final CTA band. Dark dp-* styling, claim-safe copy.
 */

export function CompatPreview() {
  const { t } = useTranslations();
  const devices = ["iPhone", "Google Pixel", "Samsung Galaxy"];
  return (
    <section className="container mx-auto px-4 py-8">
      <Reveal as="div" className="dp-card p-6 sm:p-8 grid lg:grid-cols-2 gap-6 items-center" y={20}>
        <div>
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl dp-chip text-primary mb-3"><Smartphone className="w-6 h-6" /></span>
          <h2 className="text-2xl text-foreground">{t("dp.compatPrev.title1", "Works with your")} <span className="dp-neon">{t("dp.compatPrev.title2", "device")}</span></h2>
          <p className="mt-2 text-sm text-muted-foreground">{t("dp.compatPrev.sub", "Compatible with eSIM-supported smartphones and tablets. Check yours in seconds.")}</p>
          <Link href="/compatible-phones/" className="mt-4 inline-flex items-center gap-2 rounded-full border border-border text-foreground font-bold px-5 h-11 hover:border-primary/50 transition-all hover:-translate-y-0.5">{t("dp.compatPrev.cta", "Check device")} <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <Stagger className="flex flex-wrap gap-2" staggerChildren={0.06} amount={0.3}>
          {devices.map((d) => <StaggerItem as="span" key={d} y={8} className="dp-chip inline-flex items-center px-4 h-10 text-sm font-semibold text-foreground/80">{d}</StaggerItem>)}
        </Stagger>
      </Reveal>
    </section>
  );
}

export function SupportReassure() {
  const { t } = useTranslations();
  const items = [
    { icon: ShieldCheck, t: t("dp.supportPrev.s1", "Secure & encrypted"), b: t("dp.supportPrev.s1b", "Protected checkout, every time.") },
    { icon: RefreshCcw, t: t("dp.supportPrev.s2", "Money-back guarantee"), b: t("dp.supportPrev.s2b", "Refunded if activation fails.") },
    { icon: Headset, t: t("dp.supportPrev.s3", "24/7 support"), b: t("dp.supportPrev.s3b", "Real help, whenever you travel.") },
  ];
  return (
    <Stagger as="section" className="container mx-auto px-4 py-8 grid sm:grid-cols-3 gap-4" staggerChildren={0.08} amount={0.2}>
      {items.map(({ icon: Icon, t: title, b }) => (
        <StaggerItem as="div" key={title} y={16} className="dp-card p-5 flex items-start gap-3 transition-colors hover:border-primary/40">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl dp-chip text-primary shrink-0"><Icon className="w-5 h-5" /></span>
          <div><div className="font-bold text-foreground">{title}</div><div className="text-sm text-muted-foreground">{b}</div></div>
        </StaggerItem>
      ))}
    </Stagger>
  );
}

export function FinalCta() {
  const { t } = useTranslations();
  return (
    <section className="container mx-auto px-4 py-10 pb-16">
      <Reveal as="div" className="dp-card dp-glow p-8 sm:p-12 text-center" y={24}>
        <h2 className="text-2xl sm:text-3xl text-foreground">{t("dp.finalCta.title1", "Your travel data,")} <span className="dp-neon">{t("dp.finalCta.title2", "under control.")}</span></h2>
        <p className="mt-2 text-muted-foreground max-w-xl mx-auto">{t("dp.finalCta.sub", "Choose a global, regional or country plan and get connected in minutes.")}</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link href="/destinations/" className="dp-pill inline-flex items-center gap-2 h-12 rounded-full px-7 font-bold transition-all active:scale-[0.98]">{t("dp.finalCta.cta", "Find your eSIM")} <ArrowRight className="w-4 h-4" /></Link>
          <Link href="/installation-guide/" className="inline-flex items-center gap-2 h-12 rounded-full px-5 font-bold text-foreground border border-border hover:border-primary/50 transition-all hover:-translate-y-0.5">{t("dp.finalCta.secondary", "How it works")}</Link>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-primary" />{t("dp.finalCta.b1", "24/7 support")}</span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-primary" />{t("dp.finalCta.b2", "Secure checkout")}</span>
        </div>
      </Reveal>
    </section>
  );
}
